import { Box3, Vector3 } from 'three';

/** A capsule: segment a–b with radius r. */
export interface Capsule {
  a: Vector3;
  b: Vector3;
  r: number;
  /** arc length of the track sample this capsule covers (for local exclusion) */
  s: number;
}

interface BVHNode {
  box: Box3;
  left: number; // child index or -1
  right: number;
  start: number; // leaf: range into items
  count: number;
}

/**
 * BVH over track capsules. Capsule-vs-capsule is a segment–segment distance
 * test, which is what makes the support solver's no-clip rule fast enough to
 * run interactively.
 */
export class CapsuleBVH {
  readonly capsules: Capsule[];
  private nodes: BVHNode[] = [];
  private order: number[] = [];

  constructor(capsules: Capsule[]) {
    this.capsules = capsules;
    this.order = capsules.map((_, i) => i);
    if (capsules.length > 0) this.build(0, capsules.length);
  }

  private build(start: number, count: number): number {
    const box = new Box3();
    const tmp = new Box3();
    for (let i = start; i < start + count; i++) {
      capsuleBox(this.capsules[this.order[i]], tmp);
      box.union(tmp);
    }
    const nodeIndex = this.nodes.length;
    this.nodes.push({ box, left: -1, right: -1, start, count });
    if (count <= 4) return nodeIndex;

    const size = new Vector3();
    box.getSize(size);
    const axis: 'x' | 'y' | 'z' = size.x > size.y ? (size.x > size.z ? 'x' : 'z') : size.y > size.z ? 'y' : 'z';
    const slice = this.order.slice(start, start + count);
    slice.sort((ia, ib) => {
      const ca = (this.capsules[ia].a[axis] + this.capsules[ia].b[axis]) / 2;
      const cb = (this.capsules[ib].a[axis] + this.capsules[ib].b[axis]) / 2;
      return ca - cb;
    });
    for (let i = 0; i < count; i++) this.order[start + i] = slice[i];

    const half = count >> 1;
    const node = this.nodes[nodeIndex];
    node.left = this.build(start, half);
    node.right = this.build(start + half, count - half);
    node.count = 0; // interior
    return nodeIndex;
  }

  /**
   * Does the capsule (a, b, r) hit any track capsule, excluding those whose
   * arc length lies within `excludeS ± excludeRange` (the neighbourhood the
   * column legitimately attaches to)?
   */
  intersectsCapsule(a: Vector3, b: Vector3, r: number, excludeS = -1, excludeRange = 0): boolean {
    if (this.nodes.length === 0) return false;
    const queryBox = new Box3().expandByPoint(a).expandByPoint(b).expandByScalar(r);
    const stack = [0];
    const totalS = this.maxS();
    while (stack.length > 0) {
      const node = this.nodes[stack.pop()!];
      if (!node.box.intersectsBox(queryBox)) continue;
      if (node.count > 0) {
        for (let i = node.start; i < node.start + node.count; i++) {
          const c = this.capsules[this.order[i]];
          if (excludeS >= 0 && arcDist(c.s, excludeS, totalS) < excludeRange) continue;
          const d = segmentSegmentDistance(a, b, c.a, c.b);
          if (d < r + c.r) return true;
        }
      } else {
        stack.push(node.left, node.right);
      }
    }
    return false;
  }

  private cachedMaxS = -1;
  private maxS(): number {
    if (this.cachedMaxS < 0) {
      let m = 0;
      for (const c of this.capsules) m = Math.max(m, c.s);
      this.cachedMaxS = m;
    }
    return this.cachedMaxS;
  }
}

function arcDist(s0: number, s1: number, total: number): number {
  const d = Math.abs(s0 - s1);
  return Math.min(d, Math.abs(total - d));
}

function capsuleBox(c: Capsule, out: Box3): Box3 {
  out.makeEmpty();
  out.expandByPoint(c.a).expandByPoint(c.b).expandByScalar(c.r);
  return out;
}

const _d1 = new Vector3();
const _d2 = new Vector3();
const _rv = new Vector3();
const _c1 = new Vector3();
const _c2 = new Vector3();

/** Closest distance between segments p1–q1 and p2–q2 (Ericson, RTCD 5.1.9). */
export function segmentSegmentDistance(p1: Vector3, q1: Vector3, p2: Vector3, q2: Vector3): number {
  return segmentSegmentClosest(p1, q1, p2, q2).dist;
}

export interface SegmentClosest {
  dist: number;
  /** Parameter on segment 1 (0..1). */
  s: number;
  /** Parameter on segment 2 (0..1). */
  t: number;
  c1: Vector3;
  c2: Vector3;
}

/** Closest points between segments p1–q1 and p2–q2 (Ericson, RTCD 5.1.9). */
export function segmentSegmentClosest(
  p1: Vector3,
  q1: Vector3,
  p2: Vector3,
  q2: Vector3,
): SegmentClosest {
  _d1.subVectors(q1, p1);
  _d2.subVectors(q2, p2);
  _rv.subVectors(p1, p2);
  const a = _d1.dot(_d1);
  const e = _d2.dot(_d2);
  const f = _d2.dot(_rv);
  let s: number;
  let t: number;
  if (a <= 1e-12 && e <= 1e-12) {
    return { dist: _rv.length(), s: 0, t: 0, c1: p1.clone(), c2: p2.clone() };
  }
  if (a <= 1e-12) {
    s = 0;
    t = clamp01(f / e);
  } else {
    const c = _d1.dot(_rv);
    if (e <= 1e-12) {
      t = 0;
      s = clamp01(-c / a);
    } else {
      const b = _d1.dot(_d2);
      const denom = a * e - b * b;
      s = denom > 1e-12 ? clamp01((b * f - c * e) / denom) : 0;
      t = (b * s + f) / e;
      if (t < 0) {
        t = 0;
        s = clamp01(-c / a);
      } else if (t > 1) {
        t = 1;
        s = clamp01((b - c) / a);
      }
    }
  }
  _c1.copy(p1).addScaledVector(_d1, s);
  _c2.copy(p2).addScaledVector(_d2, t);
  return {
    dist: _c1.distanceTo(_c2),
    s,
    t,
    c1: _c1.clone(),
    c2: _c2.clone(),
  };
}

function clamp01(x: number): number {
  return Math.min(Math.max(x, 0), 1);
}
